import { Box, Collapse, IconButton, Stack, Typography } from "@mui/material";
import { ExpandMore, LocalShipping } from "@mui/icons-material";
import { useState } from "react";
import IntegrationCenterTrackingOrderLink from "./IntegrationCenterTrackingOrderLink";
import { WarehouseOrderItemDto } from "@lib/ShiOneClient";

interface TrackingItem {
  itemId?: string;
  itemName?: string;
  variant?: string;
  serialNum?: string;
  assetTag?: string;
  qty?: number;
  carrierName?: string;
  items?: Array<WarehouseOrderItemDto>;
}

interface TrackingNumber {
  trackNum?: string;
  deliveryMethod?: string;
  items?: TrackingItem[];
}

interface CollapsibleListProps {
  items: string[];
  itemKey: string;
  label: string;
  singularLabel: string;
  isExpanded: boolean;
  onToggle: () => void;
}

function CollapsibleList({
  items,
  itemKey,
  label,
  singularLabel,
  isExpanded,
  onToggle,
}: CollapsibleListProps) {
  if (!items || items.length === 0) return null;

  const hasMultiple = items.length > 1;

  if (!hasMultiple) {
    return (
      <Typography variant="body2" color="text.secondary">
        {singularLabel}: {items[0]}
      </Typography>
    );
  }

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{ cursor: "pointer" }}
        onClick={onToggle}
      >
        <Typography variant="body2" color="text.secondary">
          {label} ({items.length})
        </Typography>
        <IconButton
          size="small"
          sx={{
            p: 0,
            transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
          }}
          aria-label={isExpanded ? `Collapse ${label.toLowerCase()}` : `Expand ${label.toLowerCase()}`}
        >
          <ExpandMore fontSize="small" />
        </IconButton>
      </Stack>
      <Collapse
        aria-expanded={isExpanded}
        in={isExpanded}
        timeout="auto"
        unmountOnExit
      >
        <Box sx={{ mt: 0.5, ml: 1 }}>
          {items.map((item, idx) => (
            <Typography
              key={`${itemKey}-${idx}`}
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "0.875rem" }}
            >
              • {item}
            </Typography>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

export default function IntegrationCenterTrackingTransferIntegration({
  isActiveStep,
  trackingNumbers,
}: Readonly<{
  isActiveStep: boolean;
  trackingNumbers: TrackingNumber[];
}>) {
  const hasContent = trackingNumbers && trackingNumbers.length > 0;
  const [expandedTracking, setExpandedTracking] = useState<
    Record<string, boolean>
  >({});
  const [expandedSerialNumbers, setExpandedSerialNumbers] = useState<
    Record<string, boolean>
  >({});
  const [expandedAssetTags, setExpandedAssetTags] = useState<
    Record<string, boolean>
  >({});

  const toggleTracking = (trackNum: string) => {
    setExpandedTracking((prev) => ({
      ...prev,
      [trackNum]: !(prev[trackNum] ?? true),
    }));
  };

  const toggleList = (
    key: string,
    setter: React.Dispatch<React.SetStateAction<Record<string, boolean>>>
  ) => {
    setter((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <>
      {hasContent && isActiveStep && (
        <Box
          sx={{
            p: 2,
            mt: 2,
            backgroundColor: "background.paper",
          }}
        >
          {trackingNumbers.map((tracking, trackingIdx) => {
            const itemCount =
              tracking.items?.reduce((sum, item) => sum + (item.qty || 1), 0) ||
              0;
            const carrierName = tracking.items?.[0]?.carrierName || "";
            const trackingKey =
              tracking.trackNum || `${tracking.deliveryMethod}-${trackingIdx}`;
            const isExpanded = expandedTracking[trackingKey] ?? true;

            return (
              <Box key={trackingKey} sx={{ mb: 0 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 0 }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    sx={{ flexWrap: "wrap" }}
                  >
                    <LocalShipping
                      sx={{ color: "text.secondary", mr: 1 }}
                      aria-hidden="true"
                    />
                    <Typography variant="body2" sx={{ mr: 1 }}>
                      {carrierName}
                      {tracking.deliveryMethod &&
                        ` - ${tracking.deliveryMethod}`}
                    </Typography>
                    <IntegrationCenterTrackingOrderLink tracking={tracking} />
                  </Stack>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ flexShrink: 0 }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {itemCount} item
                      {itemCount === 1 ? "" : "s"}
                    </Typography>
                    <IconButton
                      size="small"
                      disabled={!isActiveStep}
                      aria-label={
                        isExpanded
                          ? "Collapse tracking details"
                          : "Expand tracking details"
                      }
                      aria-expanded={isExpanded}
                      aria-controls={`tracking-items-${trackingKey}`}
                      sx={{
                        transform: isExpanded
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                        transition: "transform 0.2s",
                      }}
                      onClick={() => toggleTracking(trackingKey)}
                    >
                      <ExpandMore />
                    </IconButton>
                  </Stack>
                </Stack>
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Box
                    id={`tracking-items-${trackingKey}`}
                    role="region"
                    aria-label="Tracking items"
                    sx={{
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                      mt: 2,
                    }}
                  >
                    <Box
                      role="row"
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr auto",
                        px: 1.5,
                        py: 0.75,
                        bgcolor: "table.header",
                        borderBottom: 1,
                        borderColor: "divider",
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        component="div"
                        role="columnheader"
                      >
                        Item
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        component="div"
                        role="columnheader"
                        sx={{ textAlign: "right" }}
                      >
                        Quantity
                      </Typography>
                    </Box>
                    {tracking.items?.map((item, idx) => {
                      const serialNumbers = item.items
                        ?.map((nestedItem: any) => nestedItem.serialNum)
                        .filter(Boolean);
                      const assetTags = item.items
                        ?.map((nestedItem: any) => nestedItem.assetTag)
                        .filter(Boolean);

                      const itemKey = `${item.itemId || item.itemName}-${idx}`;

                      return (
                        <Box
                          key={itemKey}
                          role="row"
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr auto",
                            alignItems: "center",
                            px: 1.5,
                            py: 0.75,
                            borderBottom:
                              idx < (tracking.items?.length || 0) - 1 ? 1 : 0,
                            borderColor: "divider",
                          }}
                        >
                          <Stack role="cell">
                            <Typography variant="body2">
                              {item.itemName || "Unknown Item"}
                            </Typography>
                            <CollapsibleList
                              items={serialNumbers || []}
                              itemKey={`${itemKey}-serial`}
                              label="Serial numbers"
                              singularLabel="Serial number"
                              isExpanded={expandedSerialNumbers[itemKey] ?? false}
                              onToggle={() => toggleList(itemKey, setExpandedSerialNumbers)}
                            />
                            <CollapsibleList
                              items={assetTags || []}
                              itemKey={`${itemKey}-asset`}
                              label="Asset tags"
                              singularLabel="Asset tag"
                              isExpanded={expandedAssetTags[itemKey] ?? false}
                              onToggle={() => toggleList(itemKey, setExpandedAssetTags)}
                            />
                          </Stack>
                          <Typography
                            variant="body2"
                            role="cell"
                            sx={{ textAlign: "right" }}
                          >
                            {item.qty || 1}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Box>
      )}
    </>
  );
}
